import { createContext, MutableRefObject } from 'react';
import TypedEmitter from 'typed-emitter';
import { CallAccessAttributes, HubPatient, PaymentRequestCreatedMessage } from 'types';
import { FaxStatusMessageDto } from 'types/fax';
import { HubConnection } from '@microsoft/signalr';
import { ChatMessagePayload, ChatTypingPayload } from 'types/chat';

export type EventType =
  | 'emdrStarted'
  | 'whiteboardStarted'
  | 'callEnded'
  | 'removedFromCall'
  | 'disconnected'
  | 'reconnected'
  | 'paymentRequestCancelled'
  | 'paymentRequestSucceeded'
  | 'Transferred'
  | 'faxStatusUpdated'
  | 'requeued';
export type EventsMap = { [key in EventType]: (() => void | null)[] };

export type SignalrConnectionEvents = {
  callPreparing: () => void;
  joinCall: (roomName: string, connectionId: string) => void;
  isProviderInQueueSent: (isProviderInQueue: boolean) => void;
  updatedStatusDataSent: () => void;
  updatedUnavailableProviderStatus: (unavailable: boolean) => void;
  muted: (muted: boolean) => void;
  userJoined: (data: HubPatient) => void;
  paymentRequestCreated: (data: PaymentRequestCreatedMessage) => void;
  paymentRequestCancelled: () => void;
  paymentRequestSucceeded: () => void;
  Transferred: (newRoomUrl: string) => void;
  faxStatusUpdated: (message: FaxStatusMessageDto) => void;
  faxReceived: () => void;
  requeued: () => void;
  receivePrivateMessage: (message: ChatMessagePayload) => void;
  receiveRoomMessage: (message: ChatMessagePayload) => void;
  privateTypingStarted: (message: ChatTypingPayload) => void;
  privateTypingEnded: (message: ChatTypingPayload) => void;
  roomTypingStarted: (message: ChatTypingPayload) => void;
  roomTypingEnded: (message: ChatTypingPayload) => void;
};

export interface ISignalrConnectionContext {
  signalrConnection: React.MutableRefObject<HubConnection | null>;
  getPatientsList(): void;
  getIsProviderConnectedToQueue(): void;
  getToken(data: { identity: string; roomId: string }): Promise<string>;
  joinRoomChat(roomName: string): Promise<void>;
  sendPrivateMessage(data: { targetConnectionId: string; message: string; clientMessageId?: string }): Promise<void>;
  sendPrivateMessageByUniqueName(data: {
    uniqueName: string;
    message: string;
    clientMessageId?: string;
  }): Promise<void>;
  sendRoomMessage(data: { roomName: string; message: string; clientMessageId?: string }): Promise<void>;
  startTypingPrivate(targetConnectionId: string): Promise<void>;
  endTypingPrivate(targetConnectionId: string): Promise<void>;
  startTypingRoom(roomName: string): Promise<void>;
  endTypingRoom(roomName: string): Promise<void>;
  invitePatient(data: { connectionId: string; roomId: string }): void;
  notifyCallPreparing(connectionId: string): void;
  removeParticipantFromCall(connectionId: string): void;
  requeuePatient(connectionId: string): void;
  isRequeuing: MutableRefObject<boolean>;
  mutePatientInCall(connectionId: string, muted: boolean): void;
  updateCallData(data: { callAccesses: CallAccessAttributes }): void;
  updateUnavailableProviderStatus(unavailable: boolean): void;
  removePatientFromQueue(connectionId: string): void;
  startEmdr(): void;
  startWhiteboard(): void;
  cancelRequestPaymentByProvider(): void;
  cancelRequestPaymentByClient(): void;
  setSuccessRequestPayment(): void;
  disconnect(): void;
  isFetching: boolean;
  events: TypedEmitter<SignalrConnectionEvents>;
  signalrConnectionId: string;
  callEnded(): void;
  addEventListener(eventType: EventType, callback: () => void): void;
  removeEventListener(eventType: EventType, callback: () => void): void;
  removeAllEventListeners(): void;
  sendMessage(event: string, data: any): void;
}

export const SignalrConnectionContext = createContext<ISignalrConnectionContext | null>(null);
export const SignalrConnectionConsumer = SignalrConnectionContext.Consumer;
